import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  CAMERA_CATEGORY_COLORS,
  CAMERA_OVERLAY_COLOR,
  cameraCategoryLabel,
  type CameraLocationProperties,
} from '@signals/app-shell/services/cameraOverlayService';
import {
  IntelChip,
  IntelPopupCard,
  INTEL_COLORS,
  humanize,
  type IntelAction,
  type IntelRow,
} from '@signals/app-shell/components/map/IntelPopup';
import StreetViewThumb from '@signals/app-shell/components/map/StreetViewThumb';
import {
  cameraHeading,
  streetViewDeepLink,
  streetViewPreviewsOn,
  useCameraStreetViewSettings,
} from '@signals/app-shell/services/cameraStreetViewService';

type Props = {
  camera: CameraLocationProperties;
  /** Kept for call-site compatibility; both maps now render the same intel card. */
  variant?: 'mui' | 'plain';
  onClose?: () => void;
  /** Extra uppercase action links appended after the defaults. */
  actions?: IntelAction[];
  /** Rows inserted right after the header block (e.g. COP: entities near this camera). */
  extraRows?: IntelRow[];
};

const ATTR_LABELS: Record<string, string> = {
  formatted_address: 'Address',
  location: 'Location',
  query: 'Query',
  accuracy: 'Accuracy',
  accuracy_type: 'Acc. type',
  source_row: 'Row',
  error: 'Error',
  osm_id: 'OSM id',
  osm_type: 'OSM type',
  'surveillance:type': 'Surv. type',
  'surveillance:zone': 'Zone',
  'camera:type': 'Camera',
  'camera:mount': 'Mount',
  'camera:direction': 'Direction',
  direction: 'Direction',
  operator: 'Operator',
  manufacturer: 'Make',
  brand: 'Brand',
  man_made: 'Man made',
  geometry_type: 'Geometry',
};

/** Attribute keys that duplicate the header or are internal. */
const HIDDEN_ATTRS = new Set(['marker_type', 'overlay_file_id', 'overlay_file_name', 'man_made', 'category', 'source']);

/** Shown first when present; everything else is behind "more". */
const PRIORITY_ATTRS = ['operator', 'surveillance:type', 'camera:type', 'camera:mount', 'camera:direction', 'direction', 'manufacturer', 'brand', 'formatted_address', 'location'];

const MAX_INLINE_ATTRS = 4;

function labelFor(key: string): string {
  return ATTR_LABELS[key] || humanize(key);
}

function prettyValue(key: string, value: unknown): string {
  const s = String(value);
  if (key === 'camera:direction' || key === 'direction') {
    const n = Number(s);
    return Number.isFinite(n) ? `${Math.round(n)}°` : s;
  }
  // OSM-style enum values (pan_tilt_zoom, red_light) read better humanized.
  if (/^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(s)) return humanize(s);
  return s;
}

/**
 * Camera popup body shared by Pathfinder and COP — one intel card.
 */
const CameraLocationPopupContent: React.FC<Props> = ({ camera, onClose, actions = [], extraRows = [] }) => {
  const [showAll, setShowAll] = useState(false);
  const categoryColor = (camera.category && CAMERA_CATEGORY_COLORS[camera.category]) || CAMERA_OVERLAY_COLOR;
  const categoryLabel = cameraCategoryLabel(camera.category);
  const { settings: streetView } = useCameraStreetViewSettings();
  const heading = useMemo(() => cameraHeading(camera), [camera]);

  const attrEntries = useMemo(() => {
    const entries = Object.entries(camera.attributes || {}).filter(
      ([key, value]) => !HIDDEN_ATTRS.has(key) && value != null && String(value) !== ''
    );
    const rank = (k: string) => {
      const i = PRIORITY_ATTRS.indexOf(k);
      return i === -1 ? PRIORITY_ATTRS.length : i;
    };
    return entries.sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]));
  }, [camera.attributes]);

  const inline = showAll ? attrEntries : attrEntries.slice(0, MAX_INLINE_ATTRS);
  const hiddenCount = attrEntries.length - inline.length;

  const location = (camera.location || '').trim();
  const rows: IntelRow[] = [
    ...extraRows,
    // Title shows the road / intersection when derived; keep the id reachable.
    { label: 'ID', value: location ? camera.camera_id : null, mono: true },
    { label: 'Name', value: camera.camera_name && camera.camera_name !== camera.camera_id ? camera.camera_name : null },
    {
      label: 'Category',
      value: <IntelChip color={categoryColor} filled>{categoryLabel}</IntelChip>,
    },
    { label: 'Source', value: camera.source },
    { label: 'Status', value: camera.status ? humanize(camera.status) : null, color: INTEL_COLORS.green },
    ...inline.map(([key, value]) => ({ label: labelFor(key), value: prettyValue(key, value) })),
  ];

  const hasCoords = Number.isFinite(camera.latitude) && Number.isFinite(camera.longitude);
  const streetViewOn = Boolean(streetView?.enabled) && hasCoords;
  const previewsOn = streetViewPreviewsOn(streetView) && hasCoords;

  const allActions: IntelAction[] = [
    ...(hiddenCount > 0 || showAll
      ? [{ label: showAll ? 'Less' : `+${hiddenCount} more`, onClick: () => setShowAll((v) => !v), color: INTEL_COLORS.textMuted }]
      : []),
    ...(streetViewOn
      ? [{
          label: 'Street View',
          href: streetViewDeepLink(camera.latitude, camera.longitude, heading),
          title: heading == null ? 'Open Google Street View here' : `Open Google Street View facing ${Math.round(heading)}°`,
        }]
      : []),
    ...actions,
  ];

  return (
    <IntelPopupCard
      title={location || camera.camera_id || camera.camera_name || 'Camera'}
      chip={<IntelChip>Camera</IntelChip>}
      accent={categoryColor}
      media={
        previewsOn ? (
          <StreetViewThumb lat={camera.latitude} lon={camera.longitude} heading={heading} enabled />
        ) : undefined
      }
      rows={rows}
      coords={hasCoords ? { lat: camera.latitude, lon: camera.longitude } : null}
      actions={allActions}
      onClose={onClose}
    >
      {!camera.camera_name && !attrEntries.length && (
        <Box sx={{ fontSize: '0.72rem', color: INTEL_COLORS.textDim }}>No additional attributes.</Box>
      )}
    </IntelPopupCard>
  );
};

export default CameraLocationPopupContent;
